import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
} from '@mui/material';
import './UserAgreementModal.scss';

interface UserAgreementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAgree: () => void;
  isLoading?: boolean;
}

const agreementLabels = [
  {
    id: 1,
    label: "I Agree to the GenieGig's Freelancer Code Of Conduct",
    hasLink: true,
    linkText: "(Code of Conduct)",
    linkUrl: "/code-of-conduct",
  },
  {
    id: 2,
    label:
      "I understand that this includes, but is not limited to, maintaining a high level of professionalism in all client interactions, delivering quality work on time, and adhering to the guidelines and policies set forth by the platform in the GenieGig's Freelancer Code Of Conduct",
    hasLink: false,
  },
  {
    id: 3,
    label:
      "I also understand that any violation of these standards may result in disciplinary action, up to and including termination of my account.",
    hasLink: false,
  },
  {
    id: 4,
    label:
      "By agreeing to these Terms of Service and Privacy Policy, I am committing to being a responsible, reliable, and trustworthy member of the freelancing community on GenieGig.",
    hasLink: false,
  },
];

const UserAgreementModal: React.FC<UserAgreementModalProps> = ({
  isOpen,
  onClose,
  onAgree,
  isLoading = false,
}) => {
  const [checkedState, setCheckedState] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);

  const allChecked = checkedState.every((checked) => checked);

  const handleChange = (index: number) => {
    const updatedCheckedState = [...checkedState];
    updatedCheckedState[index] = !updatedCheckedState[index];
    setCheckedState(updatedCheckedState);
  };

  // Reset checkboxes when modal opens
  useEffect(() => {
    if (isOpen) {
      setCheckedState([false, false, false, false]);
    }
  }, [isOpen]);

  const handleAgree = () => {
    if (allChecked) {
      onAgree();
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      disableEscapeKeyDown
      className="user-agreement-modal-overlay"
    >
      <Box className="user-agreement-modal-container">
        <Typography variant="h5" className="modal-title">
          Freelancer Agreement
        </Typography>
        <Typography variant="body2" className="modal-subtitle">
          Please read and accept all the terms below to complete your
          registration as a freelancer on GenieGig.
        </Typography>

        <FormGroup className="agreement-checkboxes">
          {agreementLabels.map((item, index) => (
            <FormControlLabel
              key={item.id}
              control={
                <Checkbox
                  checked={checkedState[index]}
                  onChange={() => handleChange(index)}
                  disabled={isLoading}
                  sx={{
                    color: '#002f6c',
                    '&.Mui-checked': {
                      color: '#002f6c',
                    },
                  }}
                />
              }
              label={
                item.hasLink ? (
                  <span className="checkbox-label">
                    {item.label}&nbsp;
                    <a
                      href={item.linkUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="agreement-link"
                    >
                      <u>{item.linkText}</u>
                    </a>
                  </span>
                ) : (
                  <span className="checkbox-label">{item.label}</span>
                )
              }
              className="agreement-checkbox-item"
            />
          ))}
        </FormGroup>

        <Box className="modal-actions">
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isLoading}
            className="cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAgree}
            disabled={!allChecked || isLoading}
            className="agree-btn"
          >
            {isLoading ? 'Processing...' : 'I Agree & Continue'}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default UserAgreementModal;
